import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, Loader2, Lock } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CodeBlock, ScoreBar } from "@/components/Primitives";

const STATUS_STYLE = {
  approved: "text-[hsl(var(--success))]",
  submitted: "text-[hsl(var(--warning))]",
  not_started: "text-muted-foreground",
};

export default function Projects() {
  const [project, setProject] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const [locked, setLocked] = useState(false);
  const { user } = useAuth();
  const noAccess = locked || (user?.access && user.access.effectivePlan !== "career_plus");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/projects/capstone");
      setProject(data);
      setOpen((o) => o || data.milestones.find((m) => m.status !== "approved")?.key || data.milestones[0].key);
    } catch (e) {
      setError(errMsg(e, "Unable to load capstone"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (key) => {
    setBusy(key);
    setError("");
    try {
      await api.post(`/projects/capstone/milestones/${key}/submit`, { content: drafts[key] || "" });
      setDrafts({ ...drafts, [key]: "" });
      await load();
    } catch (e) {
      if (e?.response?.status === 402) setLocked(true);
      setError(errMsg(e, "Unable to submit milestone"));
    } finally {
      setBusy("");
    }
  };

  if (!project) return <div className="grid place-items-center py-24" data-testid="projects-loading"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-10 fade-up" data-testid="projects-page">
      <div>
        <p className="overline text-primary">capstone project</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-display font-black tracking-tighter">{project.title}</h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-2xl">{project.summary}</p>
        <div className="mt-6 flex flex-wrap gap-8 font-mono text-xs text-muted-foreground">
          <span>{project.approvedMilestones}/{project.milestones.length} milestones approved</span>
          <span>average score {project.averageScore}/100</span>
          <span>contributes 10% of readiness</span>
        </div>
        <div className="mt-4 max-w-md"><ScoreBar value={project.progressPercent} testId="capstone-progress" /></div>
      </div>

      <div>
        <p className="overline text-muted-foreground">expected outcomes</p>
        <ul className="mt-3 space-y-1.5" data-testid="capstone-outcomes">
          {project.outcomes.map((o) => (
            <li key={o} className="text-sm flex gap-2 text-muted-foreground"><span className="text-primary font-mono">›</span>{o}</li>
          ))}
        </ul>
      </div>

      <CodeBlock
        testId="capstone-architecture"
        label="target architecture"
        code={`GitHub Actions ──build──▶ ECR ──deploy──▶ ECS Fargate service\n                                   │\n              ALB (public subnets) ─┘\n                                   └──▶ RDS Postgres (private subnets)\n\nterraform/  modules: network, ecr, ecs_service, rds\n.github/    workflows: test.yml, deploy.yml`}
      />

      {noAccess && (
        <div className="border border-border p-6 flex flex-wrap items-center justify-between gap-4" data-testid="capstone-locked">
          <div>
            <p className="font-display font-bold tracking-tight flex items-center gap-2"><Lock className="h-4 w-4 text-primary" /> Milestone reviews are a Career Plus feature</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {error || (user?.access?.trialExpired
                ? "Your free trial has ended. You can still read the brief — upgrade to submit milestones for AI review."
                : "You can read the brief and criteria. Upgrade to submit milestones for AI review.")}
            </p>
          </div>
          <Link to="/pricing" data-testid="capstone-locked-cta"><Button className="rounded-none">View plans</Button></Link>
        </div>
      )}

      <div className="space-y-px" data-testid="capstone-milestones">
        {project.milestones.map((m) => (
          <div key={m.key} data-testid={`milestone-${m.key}`} className="border border-border">
            <button
              className="w-full text-left p-6 flex flex-wrap items-start justify-between gap-4 hover:bg-accent/30 transition-colors duration-150"
              onClick={() => setOpen(open === m.key ? null : m.key)}
              data-testid={`milestone-toggle-${m.key}`}
            >
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-primary">m{m.order}</span>
                  <h2 className="font-display text-lg font-bold tracking-tight">{m.title}</h2>
                  <span className={`overline ${STATUS_STYLE[m.status]}`}>{m.status.replace("_", " ")}</span>
                </div>
                <p className="mt-2 text-sm text-muted-foreground max-w-2xl">{m.description}</p>
              </div>
              {m.submission ? (
                <span className="font-mono text-sm shrink-0 flex items-center gap-2">
                  {m.status === "approved" ? <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" /> : <Clock className="h-4 w-4 text-[hsl(var(--warning))]" />}
                  {m.submission.score}/100
                </span>
              ) : null}
            </button>

            {open === m.key && (
              <div className="border-t border-border p-6 space-y-5">
                <div>
                  <p className="overline text-muted-foreground">acceptance criteria</p>
                  <ul className="mt-2 space-y-1">
                    {m.acceptanceCriteria.map((c) => (
                      <li key={c} className="text-sm flex gap-2 text-muted-foreground"><span className="text-primary font-mono">›</span>{c}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-medium">{m.deliverable}</p>
                  <Textarea
                    data-testid={`milestone-input-${m.key}`}
                    rows={8}
                    className="mt-3 rounded-none font-mono text-xs"
                    placeholder="Paste your work here…"
                    value={drafts[m.key] || ""}
                    onChange={(e) => setDrafts({ ...drafts, [m.key]: e.target.value })}
                  />
                  <Button
                    className="mt-3 rounded-none gap-2"
                    disabled={busy === m.key || (drafts[m.key] || "").trim().length < 30}
                    onClick={() => submit(m.key)}
                    data-testid={`milestone-submit-${m.key}`}
                  >
                    {busy === m.key && <Loader2 className="h-4 w-4 animate-spin" />} Submit for AI review
                  </Button>
                </div>
                {m.submission && (
                  <div className="border border-border bg-accent/40 p-4 text-xs whitespace-pre-wrap font-mono leading-relaxed" data-testid={`milestone-review-${m.key}`}>
                    {m.submission.review}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
